/**
 * Centralised environment configuration.
 *
 * Every environment-dependent value enters the frontend through this module. No
 * other file reads `import.meta.env` directly, so the set of things that vary by
 * environment is enumerable in one place.
 *
 * Everything resolved here is PUBLIC. It ships in the browser bundle. Secrets
 * (the phrase HMAC key, the PIN pepper, the payload encryption key, Stripe keys)
 * are read by Cloud Functions from Secret Manager and must never reach this file.
 * See docs/security-model.md.
 */

/**
 * @typedef {"development" | "staging" | "production"} EnvironmentName
 */

/**
 * The Firebase web configuration. Every value here is public by design — the web
 * SDK ships it to the browser, and Firebase's security model does not rely on it
 * being secret (access is controlled by Auth, Firestore rules and App Check, not
 * by hiding the apiKey). Genuine secrets live in Secret Manager, server-side.
 *
 * @typedef {object} FirebaseConfig
 * @property {string} apiKey
 * @property {string} authDomain
 * @property {string} projectId
 * @property {string} appId
 */

/**
 * @typedef {object} KinCodeConfig
 * @property {EnvironmentName} environment The deployment environment this build targets.
 * @property {boolean} useEmulators Whether to connect to the Firebase Local Emulator Suite.
 * @property {FirebaseConfig | null} firebase The Firebase web configuration, or
 *   null when none is supplied. When null the application uses in-memory mock
 *   services (the Phase 1 prototype build and the accessibility/e2e build); when
 *   present it uses the Firebase-backed services.
 * @property {string | null} appCheckSiteKey The reCAPTCHA Enterprise site key for
 *   App Check, or null to run without attestation. Public by design — it is
 *   meant to be visible in the page. Optional on purpose: the app must work
 *   without it, because App Check is a defence in depth and never a requirement
 *   for retrieval (docs/security-review.md).
 */

const ENVIRONMENT_NAMES = ["development", "staging", "production"];

/**
 * The Firebase web-config fields KinCode requires, mapped to their environment
 * variable names. All are needed for Authentication and Firestore; a partial set
 * is treated as a misconfiguration rather than silently ignored.
 *
 * @type {Record<keyof FirebaseConfig, string>}
 */
const FIREBASE_ENV_KEYS = {
  apiKey: "VITE_FIREBASE_API_KEY",
  authDomain: "VITE_FIREBASE_AUTH_DOMAIN",
  projectId: "VITE_FIREBASE_PROJECT_ID",
  appId: "VITE_FIREBASE_APP_ID",
};

/**
 * @param {string | undefined} value
 * @returns {value is EnvironmentName}
 */
function isEnvironmentName(value) {
  return value === "development" || value === "staging" || value === "production";
}

/**
 * Resolve the target environment from a raw environment record.
 *
 * Accepts the source record as an argument rather than reading `import.meta.env`
 * itself, so it can be tested without a build step.
 *
 * @param {Record<string, string | undefined>} source
 * @returns {EnvironmentName}
 * @throws {Error} If the value is absent or unrecognised.
 */
export function resolveEnvironmentName(source) {
  const raw = source.VITE_KINCODE_ENVIRONMENT;
  if (!isEnvironmentName(raw)) {
    throw new Error(
      `VITE_KINCODE_ENVIRONMENT must be one of: ${ENVIRONMENT_NAMES.join(", ")}. ` +
        `Received: ${raw === undefined ? "nothing" : JSON.stringify(raw)}.`,
    );
  }
  return raw;
}

/**
 * Resolve whether to use the Firebase emulators.
 *
 * Only the exact string "true" enables them: a stray or misspelled value must
 * fail closed, pointing at real infrastructure rather than silently at an
 * emulator.
 *
 * A production build is never permitted to target emulators. That combination
 * can only arise from a misconfigured deploy, and failing the build is cheaper
 * than discovering it in production.
 *
 * @param {Record<string, string | undefined>} source
 * @returns {boolean}
 * @throws {Error} If a production build requests emulators.
 */
export function resolveUseEmulators(source) {
  const useEmulators = source.VITE_USE_FIREBASE_EMULATORS === "true";
  if (useEmulators && resolveEnvironmentName(source) === "production") {
    throw new Error(
      "Refusing to build: VITE_USE_FIREBASE_EMULATORS is true in a production environment.",
    );
  }
  return useEmulators;
}

/**
 * Resolve the Firebase web configuration from a raw environment record.
 *
 * Returns null when no `VITE_FIREBASE_*` variable is set: that is the intended
 * signal to run on the mock services (the Phase 1 prototype and the e2e build have
 * no backend). If some but not all of the required variables are present the
 * configuration is incomplete, which almost always means a broken deploy — that
 * fails loudly rather than falling back to mocks and hiding the mistake.
 *
 * @param {Record<string, string | undefined>} source
 * @returns {FirebaseConfig | null}
 * @throws {Error} If the configuration is partially specified.
 */
export function resolveFirebaseConfig(source) {
  const entries = Object.entries(FIREBASE_ENV_KEYS);
  const present = entries.filter(([, envKey]) => {
    const value = source[envKey];
    return typeof value === "string" && value.length > 0;
  });

  if (present.length === 0) {
    return null;
  }

  if (present.length !== entries.length) {
    const missing = entries
      .filter(([, envKey]) => !(typeof source[envKey] === "string" && source[envKey]))
      .map(([, envKey]) => envKey);
    throw new Error(
      `Incomplete Firebase configuration. Missing: ${missing.join(", ")}. ` +
        "Set all VITE_FIREBASE_* variables, or none to use the mock services.",
    );
  }

  return {
    apiKey: /** @type {string} */ (source[FIREBASE_ENV_KEYS.apiKey]),
    authDomain: /** @type {string} */ (source[FIREBASE_ENV_KEYS.authDomain]),
    projectId: /** @type {string} */ (source[FIREBASE_ENV_KEYS.projectId]),
    appId: /** @type {string} */ (source[FIREBASE_ENV_KEYS.appId]),
  };
}

/**
 * Build the validated application configuration.
 *
 * Fails fast and loudly: a misconfigured environment should break the build, not
 * produce an application that half works.
 *
 * `VITE_FORCE_MOCKS` discards any Firebase configuration and pins the app to the
 * mock services. It is set by the `e2e` build mode (see vite.config.js) so the
 * Playwright suite cannot reach a real backend even when `.env.local` holds valid
 * credentials. It is deliberately an explicit switch rather than relying on the
 * variables being absent — "nobody has configured it yet" is not a safeguard, and
 * the day someone does configure it the tests would start writing to production
 * without a single error.
 *
 * @param {Record<string, string | undefined>} source
 * @returns {KinCodeConfig}
 */
export function loadConfig(source) {
  const forceMocks = source.VITE_FORCE_MOCKS === "true";
  const useEmulators = resolveUseEmulators(source);

  // Emulators are a dev-server concept. A *built* bundle that points at
  // 127.0.0.1 is broken for everyone who is not the developer who built it, and
  // it fails in the most confusing possible way: the site loads, the forms work,
  // and every request dies with a connection error.
  //
  // This is not hypothetical — the first Phase 5 deploy shipped exactly that,
  // because `.env.local` correctly carried `VITE_USE_FIREBASE_EMULATORS=true`
  // for local development and the build inherited it. The existing guard only
  // covered `production`, so a `development`-tagged build sailed through.
  //
  // `import.meta.env.DEV` is true only under `vite dev`, which is precisely when
  // emulators are wanted. Anything else is a mistake worth stopping the build
  // for, in keeping with this module's fail-loudly stance.
  if (useEmulators && !forceMocks && source.DEV !== "true") {
    throw new Error(
      "Refusing to build: VITE_USE_FIREBASE_EMULATORS is true outside the dev server. " +
        "A built bundle would point at 127.0.0.1 and fail for every visitor. " +
        "Set it to false (or remove it) before building for deployment.",
    );
  }

  const siteKey = source.VITE_APPCHECK_SITE_KEY;

  return {
    environment: resolveEnvironmentName(source),
    useEmulators,
    firebase: forceMocks ? null : resolveFirebaseConfig(source),
    // Absent is a valid state, unlike the Firebase config: attestation is
    // optional and its absence must not fail the build.
    appCheckSiteKey:
      forceMocks || typeof siteKey !== "string" || siteKey.length === 0 ? null : siteKey,
  };
}
